import React from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Badge,
  Avatar,
  SimpleGrid,
  Icon,
  Divider,
  useColorModeValue,
  Button,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaUniversity,
  FaCreditCard,
  FaFileAlt,
  FaCalendarAlt,
  FaCheck,
  FaTimes,
  FaClock,
  FaUserTie,
  FaBuilding,
  FaDownload,
} from "react-icons/fa";

// Motion components
const MotionBox = motion(Box);

// Glass Morphism Card Component
const GlassMorphismCard = ({ children, ...rest }) => {
  const bgColor = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)"
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.5)",
    "rgba(255, 255, 255, 0.1)"
  );

  return (
    <Box
      bg={bgColor}
      backdropFilter="blur(10px)"
      borderRadius="2xl"
      boxShadow="xl"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{ boxShadow: "2xl" }}
      {...rest}
    >
      {children}
    </Box>
  );
};

const StatusBadge = ({ status }) => {
  let color, label, icon;

  switch (status?.toLowerCase()) {
    case "verified":
      color = "green";
      label = "Verified";
      icon = FaCheck;
      break;
    case "rejected":
      color = "red";
      label = "Rejected";
      icon = FaTimes;
      break;
    case "pending":
      color = "yellow";
      label = "Pending";
      icon = FaClock;
      break;
    case "not_initiated":
      color = "blue";
      label = "Not Initiated";
      icon = FaFileAlt;
      break;
    default:
      color = "gray";
      label = status || "Unknown";
      icon = FaFileAlt;
  }

  return (
    <Badge
      display="flex"
      alignItems="center"
      px={4}
      py={2}
      borderRadius="full"
      colorScheme={color}
      variant="subtle"
      gap={2}
    >
      <Icon as={icon} boxSize={3} />
      <Text fontSize="sm" fontWeight="medium">
        {label}
      </Text>
    </Badge>
  );
};

const PayeeDetails = ({ payee, onClose }) => {
  const textColor = useColorModeValue("gray.700", "white");
  const themeColor = "#23A979";

  if (!payee) return null;

  const handleDownload = (url, fileName) => {
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'undertaking-document';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getRoleIcon = (role) => {
    switch (role?.toLowerCase()) {
      case "admin":
        return FaUserTie;
      case "property_owner":
        return FaBuilding;
      default:
        return FaUser;
    }
  };

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case "admin":
        return "purple";
      case "property_owner":
        return "blue";
      default:
        return "gray";
    }
  };

  return (
    <MotionBox
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      w="100%"
      maxW="4xl"
      mx="auto"
    >
      <GlassMorphismCard p={8}>
        <VStack spacing={6} w="100%">
          {/* Header */}
          <HStack justify="space-between" w="100%" align="start">
            <HStack spacing={4}>
              <Avatar
                size="xl"
                name={`${payee.FirstName} ${payee.LastName}`}
                bg={themeColor}
                color="white"
                fontSize="2xl"
              />
              <VStack align="start" spacing={1}>
                <Heading size="lg" color={textColor}>
                  {`${payee.FirstName} ${payee.LastName}`}
                </Heading>
                <HStack>
                  <Badge
                    colorScheme={getRoleColor(payee.Role)}
                    variant="subtle"
                    display="flex"
                    alignItems="center"
                    gap={2}
                  >
                    <Icon as={getRoleIcon(payee.Role)} boxSize={3} />
                    {payee.Role?.replace('_', ' ')}
                  </Badge>
                  {payee.Type && (
                    <Badge colorScheme="teal" variant="outline">
                      {payee.Type}
                    </Badge>
                  )}
                </HStack>
                <Text fontSize="sm" color="gray.500">
                  ID: {payee.Id} | Tenant ID: {payee.TenantId}
                </Text>
              </VStack>
            </HStack>
            
            <VStack align="end" spacing={2}>
              <StatusBadge status={payee.VerificationStatus} />
              <Button size="sm" variant="outline" onClick={onClose}>
                Close
              </Button>
            </VStack>
          </HStack>

          <Divider />

          {/* Contact Information */}
          <Box w="100%">
            <Heading size="md" mb={4} color={textColor}>
              Contact Information
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <HStack spacing={3}>
                <Icon as={FaEnvelope} color={themeColor} boxSize={5} />
                <VStack align="start" spacing={0}>
                  <Text fontSize="sm" color="gray.500">
                    Email Address
                  </Text>
                  <Text fontSize="md" color={textColor} fontWeight="medium">
                    {payee.Email}
                  </Text>
                </VStack>
              </HStack>

              <HStack spacing={3}>
                <Icon as={FaPhone} color={themeColor} boxSize={5} />
                <VStack align="start" spacing={0}>
                  <Text fontSize="sm" color="gray.500">
                    Mobile Number
                  </Text>
                  <Text fontSize="md" color={textColor} fontWeight="medium">
                    {payee.MobileNumber}
                  </Text>
                </VStack>
              </HStack>
            </SimpleGrid>
          </Box>

          <Divider />

          {/* Bank Details */}
          <Box w="100%">
            <Heading size="md" mb={4} color={textColor}>
              Banking Information
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              <HStack spacing={3}>
                <Icon as={FaUser} color={themeColor} boxSize={5} />
                <VStack align="start" spacing={0}>
                  <Text fontSize="sm" color="gray.500">
                    Beneficiary Name
                  </Text>
                  <Text fontSize="md" color={textColor} fontWeight="medium">
                    {payee.BeneficiaryName || "N/A"}
                  </Text>
                </VStack>
              </HStack>

              <HStack spacing={3}>
                <Icon as={FaUniversity} color={themeColor} boxSize={5} />
                <VStack align="start" spacing={0}>
                  <Text fontSize="sm" color="gray.500">
                    Bank Name
                  </Text>
                  <Text fontSize="md" color={textColor} fontWeight="medium">
                    {payee.BankName || "N/A"}
                  </Text>
                </VStack>
              </HStack>

              <HStack spacing={3}>
                <Icon as={FaCreditCard} color={themeColor} boxSize={5} />
                <VStack align="start" spacing={0}>
                  <Text fontSize="sm" color="gray.500">
                    Account Number
                  </Text>
                  <Text fontSize="md" color={textColor} fontWeight="medium">
                    {payee.BankAccountNo && payee.BankAccountNo !== "NA"
                      ? `****${payee.BankAccountNo.slice(-4)}`
                      : "N/A"}
                  </Text>
                </VStack>
              </HStack>

              <HStack spacing={3}>
                <Icon as={FaUniversity} color={themeColor} boxSize={5} />
                <VStack align="start" spacing={0}>
                  <Text fontSize="sm" color="gray.500">
                    IFSC Code
                  </Text>
                  <Text fontSize="md" color={textColor} fontWeight="medium">
                    {payee.IfscCode || "N/A"}
                  </Text>
                </VStack>
              </HStack>

              <HStack spacing={3}>
                <Icon as={FaFileAlt} color={themeColor} boxSize={5} />
                <VStack align="start" spacing={0}>
                  <Text fontSize="sm" color="gray.500">
                    PAN Card
                  </Text>
                  <Text fontSize="md" color={textColor} fontWeight="medium">
                    {payee.PanCard || "N/A"}
                  </Text>
                </VStack>
              </HStack>

              <HStack spacing={3}>
                <Icon as={FaFileAlt} color={themeColor} boxSize={5} />
                <VStack align="start" spacing={0}>
                  <Text fontSize="sm" color="gray.500">
                    GST Number
                  </Text>
                  <Text fontSize="md" color={textColor} fontWeight="medium">
                    {payee.GstNo || "N/A"}
                  </Text>
                </VStack>
              </HStack>
            </SimpleGrid>
          </Box>

          {/* Additional Information */}
          {(payee.LinkedAccountId || payee.UnderTakingDocumentName) && (
            <>
              <Divider />
              <Box w="100%">
                <Heading size="md" mb={4} color={textColor}>
                  Additional Information
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  {payee.LinkedAccountId && (
                    <HStack spacing={3}>
                      <Icon as={FaFileAlt} color={themeColor} boxSize={5} />
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" color="gray.500">
                          Linked Account ID
                        </Text>
                        <Text fontSize="md" color={textColor} fontWeight="medium">
                          {payee.LinkedAccountId}
                        </Text>
                      </VStack>
                    </HStack>
                  )}                  {payee.UnderTakingDocumentName && (
                    <HStack spacing={3}>
                      <Icon as={FaFileAlt} color={themeColor} boxSize={5} />
                      <VStack align="start" spacing={0} flex={1}>
                        <Text fontSize="sm" color="gray.500">
                          Undertaking Document
                        </Text>
                        <HStack>
                          <Text fontSize="md" color={textColor} fontWeight="medium">
                            {payee.UnderTakingDocumentName}
                          </Text>
                          {payee.UnderTakingDocumentUrl && (
                            <Tooltip label="Download Document" placement="top">
                              <IconButton
                                icon={<FaDownload />}
                                size="sm"
                                colorScheme="blue"
                                variant="ghost"
                                onClick={() => handleDownload(
                                  payee.UnderTakingDocumentUrl,
                                  payee.UnderTakingDocumentName
                                )}
                                aria-label="Download Undertaking Document"
                              />
                            </Tooltip>
                          )}
                        </HStack>
                      </VStack>
                    </HStack>
                  )}
                </SimpleGrid>
              </Box>
            </>
          )}

          <Divider />

          {/* Last Updated */}
          <HStack spacing={3} w="100%" justify="center">
            <Icon as={FaCalendarAlt} color="gray.400" boxSize={4} />
            <Text fontSize="sm" color="gray.500">
              Last Updated: {new Date(payee.UpdatedAt).toLocaleString()}
            </Text>
          </HStack>
        </VStack>
      </GlassMorphismCard>
    </MotionBox>
  );
};

export default PayeeDetails;
